/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import { AppBar, Box, Stack, SxProps, Theme } from "@mui/material";
import Logo from "@/src/components/Logo/Logo.tsx";
import { Link } from "react-router";
import { headerStyle } from "@/src/components/Header/headerStyle.ts";
import DesktopNav, { DesktopNavProps } from "@/src/components/Header/DesktopNav/DesktopNav";
import { ReactNode, useId } from "react";
import BurgerButton from "@/src/components/Header/BurgerButton/BurgerButton.tsx";
import MobileNav from "@/src/components/Header/MobileNav/MobileNav.tsx";
import HeaderContextProvider, { useHeader } from "@/src/components/Header/HeaderProvider.tsx";
import FocusTrap from "@mui/material/Unstable_TrapFocus";
import Toolbar from "@mui/material/Toolbar";

export type HeaderProps = {
  scrollPosition?: number;
  navItems?: DesktopNavProps["items"] | null;
  mobileNavComp?: ReactNode;
  logoLink?: {
    to: string;
    label: string;
  } | null;
  actions?: ReactNode;
  sx?: SxProps<Theme>;
};

function HeaderInner({ logoLink, actions, navItems, mobileNavComp }: HeaderProps) {
  const { mobileNavActive, setMobileNavActive, setHeaderMobileCategorySelectSlot } = useHeader();
  const mobileNavId = useId();

  return (
    <FocusTrap open={!!mobileNavActive} disableRestoreFocus>
      <AppBar
        elevation={0}
        sx={(theme) => ({
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.default,
        })}
      >
        <Toolbar sx={{ backgroundColor: "inherit" }}>
          <Stack direction={"row"} sx={headerStyle.headerInner}>
            <Stack sx={headerStyle.brand}>
              {!!logoLink ? (
                <Link to={logoLink.to} aria-label={logoLink.label}>
                  <Logo width={134} height={40} sx={headerStyle.brandLogo} />
                </Link>
              ) : (
                <Logo width={134} height={40} sx={headerStyle.brandLogo} />
              )}
            </Stack>
            <DesktopNav sx={{ display: { xs: "none", lg: "block" } }} items={navItems} />
            {!!actions && (
              <Stack direction={"row"} sx={headerStyle.actions}>
                <Box sx={{ display: { xs: "none", lg: "block" } }}>{actions}</Box>
                <BurgerButton
                  sx={{ display: { xs: "flex", lg: "none" } }}
                  aria-controls={mobileNavId}
                  aria-expanded={mobileNavActive}
                  aria-label={"Mobile Navigation öffnen"}
                  active={mobileNavActive}
                  onClick={() => setMobileNavActive?.((prev) => !prev)}
                />
              </Stack>
            )}
          </Stack>
        </Toolbar>
        <Box sx={{ display: { xs: "flex", lg: "none" } }}>
          <MobileNav id={mobileNavId} active={mobileNavActive} items={navItems} actions={actions} mobileNavComp={mobileNavComp} />
          <Box component={"div"} ref={(ele: HTMLDivElement) => setHeaderMobileCategorySelectSlot?.(ele)} />
        </Box>
      </AppBar>
    </FocusTrap>
  );
}

export default function Header(props: HeaderProps) {
  return (
    <HeaderContextProvider>
      <HeaderInner {...props} />
    </HeaderContextProvider>
  );
}
